import { useComparer } from '@/hooks/useComparer';
import { useExpandable } from '@/hooks/useExpandable';
import { useProgressiveList } from '@/hooks/useProgressiveList';
import { ComparerPrompt, ComparerPromptGroup, ComparerPrompts } from '@/types/comparer';
import { TableCell, Typography } from '@mui/material';
import { Fragment, type FC } from 'react';
import { getChangedItems, getVisibleItems, getVisibleParams, statusColors } from '../comparerUtils/comparerUtils';
import HeaderModifiedProps from '../comparerShared/HeaderModifiedProps';
import ModifiedProps from '../comparerShared/ModifiedProps';
import SectionHeader from '../comparerShared/SectionHeader';
import StatusRow from '../comparerShared/StatusRow';

type PromptsChangesProps = {
  prompts: ComparerPrompts;
};

type PromptGroupChangesProps = {
  group: ComparerPromptGroup;
};

type UngroupedPromptsChangesProps = {
  prompts: ComparerPrompt[];
};

type PromptGroupHeaderProps = {
  group?: ComparerPromptGroup;
  promptsCount: number;
  hasChildren: boolean;
  open: boolean;
  setOpen: (open: boolean) => void;
};

const PromptGroupHeader: FC<PromptGroupHeaderProps> = ({ group, promptsCount, hasChildren, open, setOpen }) => {
  const status = group?.status;
  const isRemoved = status === 'removed';
  const isAdded = status === 'added';
  const cellColor = status ? `${statusColors(status)}.main` : 'text.primary';

  return (
    <SectionHeader
      open={open}
      setOpen={setOpen}
      expandable={hasChildren}
      cells={
        <>
          <TableCell size='small' sx={{ color: isRemoved ? cellColor : 'text.primary' }}>
            {group ? (!isAdded ? group.label : '-') : ''}
          </TableCell>
          <TableCell size='small' sx={{ color: isAdded ? cellColor : 'text.primary' }}>
            {group ? (!isRemoved ? group.label : '-') : ''}
          </TableCell>
        </>
      }
    >
      <Typography variant='subtitle2' fontWeight={700}>
        {group ? `Group: ${group?.label}` : 'No group'}
      </Typography>
      <Typography variant='caption'>{`${promptsCount} prompt${promptsCount !== 1 ? 's' : ''} changed`}</Typography>
    </SectionHeader>
  );
};

const PromptChanges: FC<{ prompt: ComparerPrompt }> = ({ prompt }) => {
  const { showUnchanged } = useComparer();
  const [open, setOpen] = useExpandable(prompt.status === 'modified');

  const visibleParams = getVisibleParams(prompt.changedProps, showUnchanged);
  const expandable = visibleParams.length > 0;

  return (
    <>
      <StatusRow
        status={prompt.status}
        title={prompt.label}
        label={prompt.label}
        open={open}
        setOpen={expandable ? setOpen : undefined}
      />
      {open &&
        expandable &&
        visibleParams.map((param, index) => <ModifiedProps key={`${param.key}-${index}`} param={param} />)}
    </>
  );
};

const PromptGroupChanges: FC<PromptGroupChangesProps> = ({ group }) => {
  const { showUnchanged } = useComparer();

  const changedPrompts = getChangedItems(group.prompts);
  const hasChanges = group.status !== 'unchanged' || changedPrompts.length > 0;
  const visiblePrompts = getVisibleItems(group.prompts, showUnchanged);
  const visibleGroupProps = getVisibleParams(group.changedProps, showUnchanged);

  const [open, setOpen] = useExpandable(hasChanges);
  const progressivePrompts = useProgressiveList(visiblePrompts);

  if (!showUnchanged && group.status === 'unchanged' && !visiblePrompts.length) return <Fragment />;
  return (
    <>
      <PromptGroupHeader
        group={group}
        promptsCount={changedPrompts.length}
        hasChildren={visibleGroupProps.length > 0 || visiblePrompts.length > 0}
        open={open}
        setOpen={setOpen}
      />
      {open &&
        visibleGroupProps.map((param, index) => <HeaderModifiedProps key={`${param.key}-${index}`} param={param} />)}
      {open && progressivePrompts.map((prompt) => <PromptChanges key={prompt.id} prompt={prompt} />)}
    </>
  );
};

const UngroupedPromptsChanges: FC<UngroupedPromptsChangesProps> = ({ prompts }) => {
  const { showUnchanged } = useComparer();

  const changedPrompts = getChangedItems(prompts);
  const visiblePrompts = getVisibleItems(prompts, showUnchanged);

  const [open, setOpen] = useExpandable(changedPrompts.length > 0);
  const progressivePrompts = useProgressiveList(visiblePrompts);

  if (!visiblePrompts.length) return <Fragment />;
  return (
    <>
      <PromptGroupHeader
        promptsCount={changedPrompts.length}
        hasChildren={visiblePrompts.length > 0}
        open={open}
        setOpen={setOpen}
      />
      {open && progressivePrompts.map((prompt) => <PromptChanges key={prompt.id} prompt={prompt} />)}
    </>
  );
};

const PromptsChanges: FC<PromptsChangesProps> = ({ prompts }) => {
  return (
    <>
      {prompts.groups.map((group) => (
        <PromptGroupChanges key={group.id} group={group} />
      ))}
      <UngroupedPromptsChanges prompts={prompts.ungrouped} />
    </>
  );
};

export default PromptsChanges;
